import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useFetchDraftConditions } from '@retail/calculus/data-access';
import { DraftCondition, GroupedConditions } from '@retail/calculus/types';
import { brandLogo } from '@retail/utils';
import { useMemo } from 'react';

export const useDraftConditions = () => {
  const { assortmentId, selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();

  const { data: draftConditionsDTO = [], isLoading: draftConditionsIsLoading } =
    useFetchDraftConditions({ assortmentId });

  const draftConditions = useMemo(
    () =>
      draftConditionsDTO.map((cond) =>
        DraftCondition.fromDto(cond, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ),
    [draftConditionsDTO, selectedBrand.brandId, selectedBrand.name]
  );
  const conditionsLength = useMemo(() => draftConditions.length, [draftConditions.length]);

  const groupedConditions = useMemo(
    () =>
      draftConditions.reduce((acc, condition) => {
        const conditionLevelId =
          condition.condition.supplier?.supplierNumber ?? condition.condition.assortmentId;
        const conditionLevelName =
          condition.condition.supplier?.name ?? condition.condition.assortmentName;
        if (!acc.find((cond) => cond.assortmentOrSupplierId === conditionLevelId)) {
          acc.push({
            assortmentOrSupplierId: conditionLevelId,
            assortmentOrSupplierName:
              condition.condition.bindingType === 'ASSORTMENT'
                ? selectedOrgUnitAssortment.name
                : conditionLevelName,
            conditions: [condition],
          });
          return acc;
        }
        return acc.map((cond) => {
          if (cond.assortmentOrSupplierId === conditionLevelId) {
            return {
              ...cond,
              conditions: [...cond.conditions, condition],
            };
          }
          return cond;
        });
      }, [] as GroupedConditions[]),
    [draftConditions, selectedOrgUnitAssortment.name]
  );

  return {
    draftConditions,
    groupedConditions,
    conditionsLength,
    draftConditionsIsLoading,
  };
};
